import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNotificationProviderConfigs } from "services/hooks/rentals";

const PROVIDER_TYPES = [
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
];

const getDefaultForm = (providerType) => {
  const shared = {
    related_organization: "",
    name: "",
    is_active: true,
  };

  if (providerType === "sms") {
    return {
      ...shared,
      base_url: "",
      username: "",
      password: "",
      sender_id: "",
    };
  }

  if (providerType === "email") {
    return {
      ...shared,
      backend: "django.core.mail.backends.smtp.EmailBackend",
      host: "",
      port: 587,
      use_tls: true,
      use_ssl: false,
      host_user: "",
      host_password: "",
      default_from_email: "",
    };
  }

  return {
    ...shared,
    enabled: true,
    api_version: "v22.0",
    phone_number_id: "",
    access_token: "",
    verify_token: "",
    test_recipient: "",
    base_url: "",
  };
};

const toPayload = (providerType, form) => {
  const shared = {
    related_organization: form.related_organization ? Number(form.related_organization) : null,
    name: form.name.trim(),
    is_active: form.is_active,
  };

  if (providerType === "sms") {
    return {
      ...shared,
      base_url: form.base_url,
      username: form.username,
      password: form.password,
      sender_id: form.sender_id,
    };
  }

  if (providerType === "email") {
    return {
      ...shared,
      backend: form.backend,
      host: form.host,
      port: Number(form.port),
      use_tls: form.use_tls,
      use_ssl: form.use_ssl,
      host_user: form.host_user,
      host_password: form.host_password,
      default_from_email: form.default_from_email,
    };
  }

  return {
    ...shared,
    enabled: form.enabled,
    api_version: form.api_version,
    phone_number_id: form.phone_number_id,
    access_token: form.access_token,
    verify_token: form.verify_token,
    test_recipient: form.test_recipient,
    base_url: form.base_url,
  };
};

const ProviderConfigsTab = () => {
  const [providerType, setProviderType] = useState("sms");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(getDefaultForm("sms"));

  const {
    providerConfigs,
    isLoadingProviderConfigs,
    errorProviderConfigs,
    isSubmittingProviderConfig,
    providerConfigActionError,
    createProviderConfig,
    updateProviderConfig,
    deleteProviderConfig,
  } = useNotificationProviderConfigs(providerType);

  const providerLabel = useMemo(
    () => PROVIDER_TYPES.find((item) => item.value === providerType)?.label || "Provider",
    [providerType]
  );

  const handleProviderTypeChange = (value) => {
    setProviderType(value);
    setEditingId(null);
    setDialogOpen(false);
    setForm(getDefaultForm(value));
  };

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(getDefaultForm(providerType));
    setDialogOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...getDefaultForm(providerType),
      ...row,
      related_organization: row.related_organization ?? "",
      is_active: row.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setMessage({ type: "error", text: "Config name is required." });
      return;
    }

    const payload = toPayload(providerType, form);
    const response = editingId
      ? await updateProviderConfig(editingId, payload)
      : await createProviderConfig(payload);

    if (response.success) {
      setMessage({
        type: "success",
        text: editingId ? `${providerLabel} config updated.` : `${providerLabel} config created.`,
      });
      setDialogOpen(false);
    } else {
      setMessage({ type: "error", text: response.message || `Failed to save ${providerLabel} config.` });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${providerLabel} provider config?`)) return;
    const response = await deleteProviderConfig(id);
    if (response.success) {
      setMessage({ type: "success", text: `${providerLabel} config deleted.` });
    } else {
      setMessage({ type: "error", text: response.message || `Failed to delete ${providerLabel} config.` });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Provider Configs</Typography>
        <Box display="flex" gap={1}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              label="Provider"
              value={providerType}
              onChange={(event) => handleProviderTypeChange(event.target.value)}
            >
              {PROVIDER_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add {providerLabel}
          </Button>
        </Box>
      </Box>

      {message.text && (
        <Alert severity={message.type === "error" ? "error" : "success"} sx={{ mb: 2 }} onClose={() => setMessage({ type: "", text: "" })}>
          {message.text}
        </Alert>
      )}

      {errorProviderConfigs && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load {providerLabel.toLowerCase()} provider configs.
        </Alert>
      )}

      {providerConfigActionError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {providerConfigActionError}
        </Alert>
      )}

      <TableContainer component={Paper}>
        {isLoadingProviderConfigs ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organisation</TableCell>
                <TableCell>Provider Details</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providerConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No {providerLabel.toLowerCase()} provider configs found.</TableCell>
                </TableRow>
              ) : (
                providerConfigs.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.name || "—"}</TableCell>
                    <TableCell>{row.related_organization ?? "Global"}</TableCell>
                    <TableCell>
                      {providerType === "sms" && `${row.base_url || "—"} | ${row.username || "—"}`}
                      {providerType === "email" && `${row.host || "—"}:${row.port ?? "—"} | ${row.host_user || "—"}`}
                      {providerType === "whatsapp" && `${row.phone_number_id || "—"} | ${row.api_version || "—"}`}
                    </TableCell>
                    <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? `Edit ${providerLabel} Config` : `Create ${providerLabel} Config`}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Organisation ID (optional for global)"
            value={form.related_organization}
            onChange={(event) => setForm((prev) => ({ ...prev, related_organization: event.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Config Name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
          />

          {providerType === "sms" && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Base URL"
                value={form.base_url}
                onChange={(event) => setForm((prev) => ({ ...prev, base_url: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Username"
                value={form.username}
                onChange={(event) => setForm((prev) => ({ ...prev, username: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Password"
                value={form.password}
                onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Sender ID"
                value={form.sender_id}
                onChange={(event) => setForm((prev) => ({ ...prev, sender_id: event.target.value }))}
              />
            </>
          )}

          {providerType === "email" && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Backend"
                value={form.backend}
                onChange={(event) => setForm((prev) => ({ ...prev, backend: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Host"
                value={form.host}
                onChange={(event) => setForm((prev) => ({ ...prev, host: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                type="number"
                label="Port"
                value={form.port}
                onChange={(event) => setForm((prev) => ({ ...prev, port: event.target.value }))}
              />
              <FormControlLabel
                control={<Switch checked={Boolean(form.use_tls)} onChange={(event) => setForm((prev) => ({ ...prev, use_tls: event.target.checked }))} />}
                label="Use TLS"
              />
              <FormControlLabel
                control={<Switch checked={Boolean(form.use_ssl)} onChange={(event) => setForm((prev) => ({ ...prev, use_ssl: event.target.checked }))} />}
                label="Use SSL"
              />
              <TextField
                fullWidth
                margin="normal"
                label="Host User"
                value={form.host_user}
                onChange={(event) => setForm((prev) => ({ ...prev, host_user: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Host Password"
                value={form.host_password}
                onChange={(event) => setForm((prev) => ({ ...prev, host_password: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Default From Email"
                value={form.default_from_email}
                onChange={(event) => setForm((prev) => ({ ...prev, default_from_email: event.target.value }))}
              />
            </>
          )}

          {providerType === "whatsapp" && (
            <>
              <FormControlLabel
                control={<Switch checked={Boolean(form.enabled)} onChange={(event) => setForm((prev) => ({ ...prev, enabled: event.target.checked }))} />}
                label="Enabled"
              />
              <TextField
                fullWidth
                margin="normal"
                label="API Version"
                value={form.api_version}
                onChange={(event) => setForm((prev) => ({ ...prev, api_version: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Phone Number ID"
                value={form.phone_number_id}
                onChange={(event) => setForm((prev) => ({ ...prev, phone_number_id: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Access Token"
                value={form.access_token}
                onChange={(event) => setForm((prev) => ({ ...prev, access_token: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Verify Token"
                value={form.verify_token}
                onChange={(event) => setForm((prev) => ({ ...prev, verify_token: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Test Recipient"
                value={form.test_recipient}
                onChange={(event) => setForm((prev) => ({ ...prev, test_recipient: event.target.value }))}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Base URL"
                value={form.base_url}
                onChange={(event) => setForm((prev) => ({ ...prev, base_url: event.target.value }))}
              />
            </>
          )}

          <Box display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.is_active}
              onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={isSubmittingProviderConfig}>
            {isSubmittingProviderConfig ? <CircularProgress size={24} /> : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderConfigsTab;
